import {
  HMIComputedFormula,
  HMIField,
  HMIFieldRule,
  HMIGroupMeta,
  HMIScreenKey,
  HMIScreenMeta,
  HMISectionLayout,
  HMITemplate,
} from './types';
import { ruleReferencesField } from './rules';
import { computedFormulaReferencesField } from './computedFields';

export const DEFAULT_SECTION_LAYOUT: HMISectionLayout = { width: 6, height: 4 };
export const MAX_SECTION_WIDTH = 12;
export const MIN_SECTION_WIDTH = 2;
export const MAX_SECTION_HEIGHT = 10;
export const MIN_SECTION_HEIGHT = 2;

/** Turns a free-text label into a stable, unique field/screen key. */
export function slugify(label: string, existingKeys: Iterable<string>): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'section';
  const taken = new Set(existingKeys);
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

/**
 * Which screen a section belongs to. Falls back to inferring it from the
 * fields that reference the group, so templates authored before `screen`
 * existed on `HMIGroupMeta` still resolve correctly in the layout builder.
 */
export function resolveGroupScreen(
  template: HMITemplate,
  group: HMIGroupMeta
): HMIScreenKey | undefined {
  if (group.screen) return group.screen;
  const owningField = template.fields.find((f) => f.group === group.key);
  return owningField?.screen;
}

export function groupsForScreen(
  template: HMITemplate,
  screenKey: HMIScreenKey
): HMIGroupMeta[] {
  return template.groups
    .filter((g) => resolveGroupScreen(template, g) === screenKey)
    .sort(
      (a, b) => (a.layout?.order ?? 0) - (b.layout?.order ?? 0)
    );
}

export function fieldCountFor(
  template: HMITemplate,
  screenKey: HMIScreenKey,
  groupKey: string
): number {
  return template.fields.filter(
    (f) => f.screen === screenKey && f.group === groupKey
  ).length;
}

function nextOrder(groups: HMIGroupMeta[]): number {
  return (
    groups.reduce((max, g) => Math.max(max, g.layout?.order ?? 0), 0) + 1
  );
}

/** A screen's direct subtabs (children), or the top-level tabs when `parentKey` is left unset. */
export function childScreens(
  template: HMITemplate,
  parentKey: HMIScreenKey | undefined
): HMIScreenMeta[] {
  return template.screens.filter((s) => (s.parentKey ?? undefined) === parentKey);
}

/** Every descendant screen key (children, grandchildren, ...) under a tab, deepest-first order not guaranteed. */
export function screenDescendants(template: HMITemplate, key: HMIScreenKey): HMIScreenKey[] {
  const direct = childScreens(template, key).map((s) => s.key);
  return direct.reduce<HMIScreenKey[]>(
    (acc, childKey) => [...acc, childKey, ...screenDescendants(template, childKey)],
    []
  );
}

/** True when a tab (or any of its subtabs, recursively) owns at least one section or field. */
export function screenHasContent(template: HMITemplate, key: HMIScreenKey): boolean {
  if (template.fields.some((f) => f.screen === key)) return true;
  if (template.groups.some((g) => resolveGroupScreen(template, g) === key)) return true;
  return childScreens(template, key).some((child) => screenHasContent(template, child.key));
}

/** Sentinel used to key the top-level selection slot in a screen-chain selection map. */
export const ROOT_SCREEN_KEY = '__root__';

export interface ScreenTabLevel {
  /** Parent tab these options are subtabs of; `undefined` for the top-level row. */
  parentKey: HMIScreenKey | undefined;
  options: HMIScreenMeta[];
  activeKey: HMIScreenKey;
}

/**
 * Walks the tab -> subtab -> subtab... tree one level at a time, picking the
 * active tab at each level from `selection` (falling back to the first
 * option), and stops once a level has no children. Shared by the layout
 * builder and the live `HMIRenderer` so both navigate the same tree the same
 * way; only the active chain changes, never the overall shape of the UI.
 */
export function buildScreenChain(
  template: HMITemplate,
  selection: Record<string, HMIScreenKey>,
  isVisible: (screen: HMIScreenMeta) => boolean = () => true
): ScreenTabLevel[] {
  const chain: ScreenTabLevel[] = [];
  let parentKey: HMIScreenKey | undefined;
  // Depth guard: a well-formed template is at most a handful of levels deep;
  // this just prevents an infinite loop if `parentKey` data is ever cyclic.
  for (let depth = 0; depth < 10; depth += 1) {
    const options = childScreens(template, parentKey).filter(isVisible);
    if (options.length === 0) break;
    const key = parentKey ?? ROOT_SCREEN_KEY;
    const stored = selection[key];
    const activeKey = options.some((o) => o.key === stored) ? stored : options[0].key;
    chain.push({ parentKey, options, activeKey });
    parentKey = activeKey;
  }
  return chain;
}

/** The deepest selected tab in a chain built by `buildScreenChain` - the screen sections/fields attach to. */
export function leafScreenKey(chain: ScreenTabLevel[]): HMIScreenKey | undefined {
  return chain.length > 0 ? chain[chain.length - 1].activeKey : undefined;
}

/**
 * Adds a new tab. Pass `parentKey` to add it as a subtab nested under an
 * existing tab instead of a new top-level tab. If the parent is about to
 * gain its *first* subtab and already owns sections of its own, those
 * sections are migrated to an auto-created "Overview" subtab first, so
 * existing content never silently disappears behind the new subtab strip.
 */
export function addScreen(
  template: HMITemplate,
  label: string,
  parentKey?: HMIScreenKey
): HMITemplate {
  let working = template;
  if (parentKey) {
    const hasChildrenAlready = childScreens(working, parentKey).length > 0;
    const ownsSectionsDirectly = working.groups.some(
      (g) => resolveGroupScreen(working, g) === parentKey
    );
    if (!hasChildrenAlready && ownsSectionsDirectly) {
      const overviewKey = slugify(
        `${parentKey}_overview`,
        working.screens.map((s) => s.key)
      );
      working = {
        ...working,
        screens: [
          ...working.screens,
          { key: overviewKey, label: 'Overview', parentKey },
        ],
        groups: working.groups.map((g) =>
          resolveGroupScreen(working, g) === parentKey ? { ...g, screen: overviewKey } : g
        ),
        fields: working.fields.map((f) =>
          f.screen === parentKey ? { ...f, screen: overviewKey } : f
        ),
      };
    }
  }
  const key = slugify(label, working.screens.map((s) => s.key));
  const screen: HMIScreenMeta = { key, label: label.trim() || key, parentKey };
  return { ...working, screens: [...working.screens, screen] };
}

export function renameScreen(
  template: HMITemplate,
  key: HMIScreenKey,
  label: string
): HMITemplate {
  return {
    ...template,
    screens: template.screens.map((s) =>
      s.key === key ? { ...s, label: label.trim() || s.label } : s
    ),
  };
}

/** Reorders a tab among its siblings only (same `parentKey`), leaving other branches untouched. */
export function reorderScreen(
  template: HMITemplate,
  key: HMIScreenKey,
  direction: -1 | 1
): HMITemplate {
  const target = template.screens.find((s) => s.key === key);
  if (!target) return template;
  const siblingKeys = childScreens(template, target.parentKey).map((s) => s.key);
  const idx = siblingKeys.indexOf(key);
  const swapWith = idx + direction;
  if (idx < 0 || swapWith < 0 || swapWith >= siblingKeys.length) return template;
  const otherKey = siblingKeys[swapWith];

  const screens = [...template.screens];
  const i = screens.findIndex((s) => s.key === key);
  const j = screens.findIndex((s) => s.key === otherKey);
  [screens[i], screens[j]] = [screens[j], screens[i]];
  return { ...template, screens };
}

/** Removes a screen, every subtab nested under it, and every section/field that belongs to any of them. */
export function deleteScreen(template: HMITemplate, key: HMIScreenKey): HMITemplate {
  const toRemove = new Set<HMIScreenKey>([key, ...screenDescendants(template, key)]);
  return {
    ...template,
    screens: template.screens.filter((s) => !toRemove.has(s.key)),
    groups: template.groups.filter((g) => {
      const screen = resolveGroupScreen(template, g);
      return screen === undefined || !toRemove.has(screen);
    }),
    fields: template.fields.filter((f) => !toRemove.has(f.screen)),
  };
}

export function addSection(
  template: HMITemplate,
  screenKey: HMIScreenKey,
  label: string
): HMITemplate {
  const key = slugify(label, template.groups.map((g) => g.key));
  const siblings = groupsForScreen(template, screenKey);
  const group: HMIGroupMeta = {
    key,
    label: label.trim() || key,
    screen: screenKey,
    layout: { ...DEFAULT_SECTION_LAYOUT, order: nextOrder(siblings) },
  };
  return { ...template, groups: [...template.groups, group] };
}

export function renameSection(
  template: HMITemplate,
  key: string,
  label: string
): HMITemplate {
  return {
    ...template,
    groups: template.groups.map((g) =>
      g.key === key ? { ...g, label: label.trim() || g.label } : g
    ),
  };
}

/** Removes a section along with every field assigned to it. */
export function deleteSection(template: HMITemplate, key: string): HMITemplate {
  return {
    ...template,
    groups: template.groups.filter((g) => g.key !== key),
    fields: template.fields.filter((f) => f.group !== key),
  };
}

export function reorderSection(
  template: HMITemplate,
  screenKey: HMIScreenKey,
  key: string,
  direction: -1 | 1
): HMITemplate {
  const siblings = groupsForScreen(template, screenKey);
  const idx = siblings.findIndex((g) => g.key === key);
  const swapWith = idx + direction;
  if (idx < 0 || swapWith < 0 || swapWith >= siblings.length) return template;

  const a = siblings[idx];
  const b = siblings[swapWith];
  const aOrder = a.layout?.order ?? 0;
  const bOrder = b.layout?.order ?? 0;
  return {
    ...template,
    groups: template.groups.map((g) => {
      if (g.key === a.key) {
        return { ...g, layout: { ...DEFAULT_SECTION_LAYOUT, ...g.layout, order: bOrder } };
      }
      if (g.key === b.key) {
        return { ...g, layout: { ...DEFAULT_SECTION_LAYOUT, ...g.layout, order: aOrder } };
      }
      return g;
    }),
  };
}

export function resizeSection(
  template: HMITemplate,
  key: string,
  size: { width: number; height: number }
): HMITemplate {
  return {
    ...template,
    groups: template.groups.map((g) =>
      g.key === key
        ? {
            ...g,
            layout: {
              ...DEFAULT_SECTION_LAYOUT,
              ...g.layout,
              width: Math.max(
                MIN_SECTION_WIDTH,
                Math.min(MAX_SECTION_WIDTH, Math.round(size.width))
              ),
              height: Math.max(
                MIN_SECTION_HEIGHT,
                Math.min(MAX_SECTION_HEIGHT, Math.round(size.height))
              ),
            },
          }
        : g
    ),
  };
}

/** How many fields would be lost if this section/screen (and its subtabs) were deleted - used for confirmation copy. */
export function fieldsUnderScreen(template: HMITemplate, screenKey: HMIScreenKey): HMIField[] {
  const scope = new Set<HMIScreenKey>([screenKey, ...screenDescendants(template, screenKey)]);
  return template.fields.filter((f) => scope.has(f.screen));
}

/** How many sections would be lost if this tab (and its subtabs) were deleted - used for confirmation copy. */
export function sectionsUnderScreen(template: HMITemplate, screenKey: HMIScreenKey): HMIGroupMeta[] {
  const scope = new Set<HMIScreenKey>([screenKey, ...screenDescendants(template, screenKey)]);
  return template.groups.filter((g) => {
    const screen = resolveGroupScreen(template, g);
    return screen !== undefined && scope.has(screen);
  });
}

/** Every field assigned to one section (group), in authoring order. */
export function fieldsForGroup(template: HMITemplate, groupKey: string): HMIField[] {
  return template.fields
    .filter((f) => f.group === groupKey)
    .sort((a, b) => a.order - b.order);
}

function nextFieldOrder(fields: HMIField[]): number {
  return fields.reduce((max, f) => Math.max(max, f.order), 0) + 1;
}

/**
 * Adds a new field to a section. `draft` holds everything the field editor
 * form collects except `id`/`screen`/`group`/`order`, which are derived here
 * so the layout builder never needs to hand-author a field id.
 */
export function addField(
  template: HMITemplate,
  screenKey: HMIScreenKey,
  groupKey: string,
  draft: Omit<HMIField, 'id' | 'screen' | 'group' | 'order'>
): HMITemplate {
  const siblings = fieldsForGroup(template, groupKey);
  const field: HMIField = {
    ...draft,
    id: slugify(draft.caption, template.fields.map((f) => f.id)),
    screen: screenKey,
    group: groupKey,
    order: nextFieldOrder(siblings),
  };
  return { ...template, fields: [...template.fields, field] };
}

/** Patches an existing field in place; `id`/`screen`/`group`/`order` are left untouched. */
export function updateField(
  template: HMITemplate,
  fieldId: string,
  patch: Partial<Omit<HMIField, 'id' | 'screen' | 'group' | 'order'>>
): HMITemplate {
  return {
    ...template,
    fields: template.fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)),
  };
}

/**
 * Removes a field, clears any `compareFieldId` reference to it on a chart
 * field (so a dual-line widget never points at a deleted series), and prunes
 * any dependency rule or computed formula that reads/writes it (to avoid
 * dangling references).
 */
export function deleteField(template: HMITemplate, fieldId: string): HMITemplate {
  return {
    ...template,
    fields: template.fields
      .filter((f) => f.id !== fieldId)
      .map((f) =>
        f.compareFieldId === fieldId ? { ...f, compareFieldId: undefined, compareLabel: undefined } : f
      ),
    rules: (template.rules ?? []).filter((r) => !ruleReferencesField(r, fieldId)),
    computedFormulas: (template.computedFormulas ?? []).filter(
      (f) => !computedFormulaReferencesField(f, fieldId)
    ),
  };
}

/** Reorders a field among its section siblings only (swaps `order` values). */
export function reorderField(
  template: HMITemplate,
  groupKey: string,
  fieldId: string,
  direction: -1 | 1
): HMITemplate {
  const siblings = fieldsForGroup(template, groupKey);
  const idx = siblings.findIndex((f) => f.id === fieldId);
  const swapWith = idx + direction;
  if (idx < 0 || swapWith < 0 || swapWith >= siblings.length) return template;

  const a = siblings[idx];
  const b = siblings[swapWith];
  return {
    ...template,
    fields: template.fields.map((f) => {
      if (f.id === a.id) return { ...f, order: b.order };
      if (f.id === b.id) return { ...f, order: a.order };
      return f;
    }),
  };
}

/** Adds a new cross-field dependency/validation rule (see `HMIFieldRule`), deriving a unique id. */
export function addRule(
  template: HMITemplate,
  draft: Omit<HMIFieldRule, 'id'>
): HMITemplate {
  const id = slugify('rule', (template.rules ?? []).map((r) => r.id));
  const rule: HMIFieldRule = { ...draft, id };
  return { ...template, rules: [...(template.rules ?? []), rule] };
}

export function updateRule(
  template: HMITemplate,
  ruleId: string,
  patch: Omit<HMIFieldRule, 'id'>
): HMITemplate {
  return {
    ...template,
    rules: (template.rules ?? []).map((r) => (r.id === ruleId ? { ...patch, id: ruleId } : r)),
  };
}

export function deleteRule(template: HMITemplate, ruleId: string): HMITemplate {
  return { ...template, rules: (template.rules ?? []).filter((r) => r.id !== ruleId) };
}

/** Adds a new computed-field formula (see `HMIComputedFormula`), deriving a unique id. */
export function addComputedFormula(
  template: HMITemplate,
  draft: Omit<HMIComputedFormula, 'id'>
): HMITemplate {
  const id = slugify('formula', (template.computedFormulas ?? []).map((f) => f.id));
  const formula: HMIComputedFormula = { ...draft, id };
  return { ...template, computedFormulas: [...(template.computedFormulas ?? []), formula] };
}

export function updateComputedFormula(
  template: HMITemplate,
  formulaId: string,
  patch: Omit<HMIComputedFormula, 'id'>
): HMITemplate {
  return {
    ...template,
    computedFormulas: (template.computedFormulas ?? []).map((f) =>
      f.id === formulaId ? { ...patch, id: formulaId } : f
    ),
  };
}

export function deleteComputedFormula(template: HMITemplate, formulaId: string): HMITemplate {
  return {
    ...template,
    computedFormulas: (template.computedFormulas ?? []).filter((f) => f.id !== formulaId),
  };
}
